/* Bridal Quote Studio™ — standalone live-preview renderer.

  This page is the consumer of the parent editor's state. It exists for
  two reasons:
    1. As an <iframe> src inside the editor → the iframe has its own
       viewport so `60vh`-style values inside BridalQuoteStudioView render
       at the iframe's size, not the editor browser tab's full screen.
       This is what lets us show a faithful "as the bride will see it"
       preview inside a constrained pane.
    2. As a target for "Apri in nuova finestra" → opens this same page in
       a new tab and reads a one-shot snapshot from localStorage.

  Data flow:
    - Embedded mode (iframe): the parent posts the latest template + the
      resolved image URL map via `window.postMessage`. We listen and
      update the local state on every keystroke / color change.
    - Standalone mode (new tab): the parent stamps a snapshot to
      localStorage under `qs-preview-snapshot:{id}` before opening this
      page; we read it once on mount. No live sync.

  Auth: this route is mounted under `/app/quote-studio/preview` so it
  inherits the `ProtectedRoute` wrapper. It does NOT hit any API — it's
  pure render of state pushed by the parent.
*/
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BridalQuoteStudioView from "@/components/BridalQuoteStudioView";
import { useAuth } from "@/context/AuthContext";

const STORAGE_PREFIX = "qs-preview-snapshot:";

/** Walk the template and replace every `r2://…` reference with the
 *  resolved URL from `imgMap` so <img src=…> renders the live preview
 *  before the user saves. */
function resolveImages(tpl, imgMap) {
  if (!tpl) return tpl;
  const clone = JSON.parse(JSON.stringify(tpl));
  const swap = (v) => (typeof v === "string" && imgMap[v] ? imgMap[v] : v);

  if (clone.cover) {
    clone.cover.image_url = swap(clone.cover.image_url);
    clone.cover.logo_url = swap(clone.cover.logo_url);
  }
  if (clone.about) clone.about.photo_url = swap(clone.about.photo_url);
  if (clone.journey) {
    for (const k of ["intro_block", "trial_block", "wedding_day_block", "products_block"]) {
      if (clone.journey[k]) clone.journey[k].image_url = swap(clone.journey[k].image_url);
    }
  }
  if (clone.portfolio && Array.isArray(clone.portfolio.images)) {
    clone.portfolio.images = clone.portfolio.images.map((im) => ({ ...im, url: swap(im?.url) }));
  }
  return clone;
}

export default function QuoteStudioPreview() {
  const [params] = useSearchParams();
  const snapshotKey = params.get("k") || "";
  const { user } = useAuth();
  const ownerName = user?.brand_name || user?.name || "";

  const [tpl, setTpl] = useState(null);
  const [imgMap, setImgMap] = useState({});

  // Standalone mode: read one-shot snapshot from localStorage on mount.
  useEffect(() => {
    if (!snapshotKey) return;
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + snapshotKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.template) setTpl(parsed.template);
        if (parsed?.previewMap) setImgMap(parsed.previewMap);
      }
    } catch (e) {
      // ignore — empty preview is acceptable
    }
  }, [snapshotKey]);

  // Embedded mode: listen for postMessage from the parent editor frame.
  // The parent posts `{ type: "qs-preview-update", template, previewMap }`
  // on every state change (debounced).
  //
  // Security: parent and iframe are ALWAYS served from the same origin
  // (both mounted under REACT_APP_BACKEND_URL — see QuoteStudio.jsx
  // <iframe src="/quote-studio/preview">). Any message coming from a
  // different origin is a spoof attempt and MUST be dropped. This also
  // matches the strict targetOrigin we pass on the outbound handshake.
  useEffect(() => {
    const trustedOrigin = window.location.origin;
    function onMsg(e) {
      if (e.origin !== trustedOrigin) return;   // reject cross-origin
      if (e.source !== window.parent) return;   // reject non-parent frames
      const d = e.data;
      if (!d || d.type !== "qs-preview-update") return;
      if (d.template) setTpl(d.template);
      if (d.previewMap) setImgMap(d.previewMap);
    }
    window.addEventListener("message", onMsg);
    // Tell the parent we're ready so it can push the initial snapshot.
    try { window.parent?.postMessage({ type: "qs-preview-ready" }, trustedOrigin); } catch (_) {}
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const resolved = tpl ? resolveImages(tpl, imgMap) : null;

  if (!resolved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-neutral-400 text-sm italic px-6 text-center">
        L'anteprima si aggiornerà non appena modifichi un campo nell'editor.
      </div>
    );
  }

  if (resolved.enabled === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-neutral-500 text-sm px-6 text-center" data-testid="qs-preview-disabled">
        Il template è in modalità <strong className="mx-1">Bozza</strong>.<br />
        Attiva l'interruttore <span className="italic">Attivo</span> in alto per pubblicarlo.
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="qs-preview-root">
      <BridalQuoteStudioView tpl={resolved} ownerName={ownerName} />
    </div>
  );
}

/* Exported helpers — used by the editor to write the snapshot for the
 * "Apri in nuova finestra" button. Kept here so the storage contract
 * lives next to its consumer. */
export function writePreviewSnapshot(template, previewMap) {
  const key = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
  try {
    localStorage.setItem(
      STORAGE_PREFIX + key,
      JSON.stringify({ template, previewMap, ts: Date.now() }),
    );
    // Garbage-collect older snapshots (>30 min) to keep localStorage tidy.
    const cutoff = Date.now() - 30 * 60 * 1000;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      try {
        const v = JSON.parse(localStorage.getItem(k) || "{}");
        if (!v.ts || v.ts < cutoff) localStorage.removeItem(k);
      } catch (_) { localStorage.removeItem(k); }
    }
  } catch (_) {
    // Quota exceeded / iframe-blocked storage → fall through. The
    // standalone page will simply show the empty state.
  }
  return key;
}
